import Element, { ElementType, RootEntityType } from '@/modules/elements/models/Element'
import { useElementStore } from '@/modules/elements/stores/ElementStore'
import Resource from '@/modules/elements/models/Resource'
import { useAppStore } from '@/modules/global/stores/AppStore'
import Comment from '@/modules/messages/models/Comment'
import Space, { SpaceVerificationMode } from '@/modules/space/models/Space'
import { useSpacePageTemplateStore } from '@/modules/space/stores/SpacePageTemplateStore'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import Task from '@/modules/task/models/Task'
import { useTaskStore } from '@/modules/task/stores/TaskStore'
import { useTaskTemplateStore } from '@/modules/task/stores/TaskTemplateStore'
import User, { TeamRole, UserState } from '@/modules/user/models/User'
import { useUserStore } from '@/modules/user/stores/UserStore'
import { isolatedPagePermission } from '@/util/isolated-page-permissions'
import { defineStore } from 'pinia'
import { Permission } from '../models/Permission'

/** True when `allElements` is the live ElementStore list (canvas hot path). */
function isStoreElementList(allElements: Element[]): boolean {
  try {
    return allElements === useElementStore().elements
  } catch {
    return false
  }
}

/** Page ids with at least one completed signature element. Shared by the locked-page getter and explicit scans. */
export function lockedSpacePageIdsFrom(elements: Element[]): Set<string> {
  const ids = new Set<string>()
  for (const e of elements) {
    if (e.type === ElementType.signature && e.rootEntityId && e.signatureList?.signatures?.some(s => s.signedAt)) {
      ids.add(e.rootEntityId)
    }
  }
  return ids
}

export const usePermissionStore = defineStore('permission', {
  state: (): {} => ({}),
  getters: {
    /** Recomputed once per element-store update; O(1) lookup via isSpacePageLocked(pageId). */
    lockedSpacePageIds(): Set<string> {
      return lockedSpacePageIdsFrom(useElementStore().elements)
    }
  },
  actions: {
    getCurrentUser(): User {
      return useUserStore().currentUser!
    },
    getUsers(): User[] {
      return useUserStore().users
    },
    isInternal(user: User | null = null): boolean {
      if (!user) user = this.getCurrentUser()
      return Boolean(user) && user?.role !== TeamRole.EXTERNAL && user?.role !== TeamRole.ANONYMOUS
    },
    isAnonymous(user: User | null = null): boolean {
      if (!user) {
        user = this.getCurrentUser()
      }
      return user?.role === TeamRole.ANONYMOUS
    },
    isInvited(space: Space, userId: string): boolean {
      return space.recipients.map(r => r.userId).includes(userId)
    },
    isExternalUser(user: User | null = null): boolean {
      if (!user) {
        user = this.getCurrentUser()
      }
      return !this.isInternal(user)
    },
    isPreviewMode(): boolean {
      return useAppStore().isInPreviewMode
    },
    isNotInternalOrPreviewMode(): boolean {
      return !this.isInternal() || this.isPreviewMode()
    },
    checkTeamPermission(permission: Permission): boolean {
      const user = this.getCurrentUser()
      if (!user) {
        return false
      }

      switch (permission) {
        case Permission.MANAGE_GLOBAL_SPACE_PAGE_TEMPLATES:
          return user.isGlobalTemplateManger
        case Permission.UPDATE_TEAM_SETTINGS:
        case Permission.MANAGE_SUBSCRIPTIONS:
        case Permission.MANAGE_DEVELOPER_CENTER:
        case Permission.VIEW_SPACE_LIST_DIRECTORY_SETTINGS:
          return user.role === TeamRole.ADMIN
        case Permission.MANAGE_SPACE_LIST:
        case Permission.MANAGE_TASK_TEMPLATES:
        case Permission.MANAGE_SPACE_PAGE_TEMPLATES:
        case Permission.MANAGE_ACTION_PLANS:
        case Permission.INVITE_TEAM_MEMBERS:
        case Permission.MANAGE_TASK:
        case Permission.UPDATE_TASK_STATUS:
        case Permission.VIEW_SPACE_LIST:
        case Permission.VIEW_UPDATES:
        case Permission.VIEW_TASKS:
        case Permission.VIEW_CONTENT_LIBRARY:
        case Permission.VIEW_NAV_BAR:
        case Permission.VIEW_DEVELOPER_CENTER:
          return this.isInternal(user) && !this.isPreviewMode()
        case Permission.VIEW_GLOBAL_NAV_BAR:
          return this.isInternal(user)
        case Permission.VIEW_SPACE_ACTIONS:
          return !this.isAnonymous(user)
        default:
          return false
      }
    },
    checkUserPermission(permission: Permission, user: User): boolean {
      const currentUser = this.getCurrentUser()
      const isSameUser = user.id === currentUser?.id
      const currentUserIsInternal = this.isInternal(currentUser)
      const currentUserIsAdmin = currentUser?.role === TeamRole.ADMIN
      const userIsExternal = this.isExternalUser(user)
      switch (permission) {
        case Permission.UPDATE_USERS:
          return currentUserIsAdmin || isSameUser || (currentUserIsInternal && userIsExternal)
        default:
          return false
      }
    },
    hasAtMostReadOnlyAccessToSpace(space: Space) {
      const user = this.getCurrentUser()
      if (!user || !space) {
        return true
      }
      const recipient = space.recipients.map(r => r.userId).find(m => m === user.id)
      if (!recipient && (user.role === TeamRole.EXTERNAL || user.role === TeamRole.ANONYMOUS)) {
        return true
      }
    },
    // TODO: _isInPreviewMode used to force reactivity refactor later
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    checkSpacePermission(permission: Permission, givenSpace?: Space | null, _isInPreviewMode?: boolean): boolean {
      const spaceStore = useSpaceStore()
      const space: Space = givenSpace || spaceStore.currentSpace!
      const user = this.getCurrentUser()

      if (!user || !space) {
        return false
      }

      const isRecipient = space?.recipients.map(r => r.userId).some(m => m === user.id)
      const publicSpace = space?.verificationMode === SpaceVerificationMode.PUBLIC
      const publicSpacePreview = publicSpace && this.isPreviewMode()

      switch (permission) {
        case Permission.SEE_SPACE:
          return isRecipient || this.isInternal() || publicSpace
        case Permission.ADD_CLIENT_RESOURCE:
        case Permission.UPDATE_TASK_STATUS:
          return isRecipient || this.isInternal() || publicSpace
        case Permission.CREATE_COMMENT:
          return isRecipient || this.isInternal()
        case Permission.MANAGE_SPACE:
        case Permission.MANAGE_ELEMENTS:
        case Permission.MANAGE_TASK:
        case Permission.MANAGE_SPACE_SETTINGS:
          return this.isInternal() && !this.isPreviewMode()
        case Permission.VIEW_SPACE_ACTIONS:
          return !this.isAnonymous(user) && !publicSpacePreview
        default:
          return false
      }
    },
    // TODO: _isInPreviewMode used to force reactivity refactor later
    checkTaskPermission(
      permission: Permission,
      task?: Task | null,
      space?: Space | null,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _isInPreviewMode?: boolean
    ): boolean {
      const user = this.getCurrentUser()

      if (!task) {
        return false
      } else if (task?.createdBy === user?.id && !this.isPreviewMode()) {
        return true
      }

      switch (permission) {
        case Permission.MANAGE_TASK:
          return space
            ? this.checkSpacePermission(Permission.MANAGE_TASK, space)
            : this.checkTeamPermission(Permission.MANAGE_TASK)
        case Permission.UPDATE_TASK_STATUS:
          return space
            ? this.checkSpacePermission(Permission.UPDATE_TASK_STATUS, space)
            : this.checkTeamPermission(Permission.UPDATE_TASK_STATUS)
        case Permission.MANAGE_ELEMENTS:
          return space
            ? this.checkSpacePermission(Permission.MANAGE_ELEMENTS, space)
            : !this.isNotInternalOrPreviewMode()
        case Permission.MANAGE_TASK_SHARED_STATUS:
          return !this.isNotInternalOrPreviewMode()
        case Permission.ADD_CLIENT_RESOURCE:
          return space
            ? this.checkSpacePermission(Permission.ADD_CLIENT_RESOURCE, space)
            : this.checkTeamPermission(Permission.ADD_CLIENT_RESOURCE)
        case Permission.VIEW_SPACE_ACTIONS:
          return space
            ? this.checkSpacePermission(Permission.VIEW_SPACE_ACTIONS, space)
            : this.checkTeamPermission(Permission.VIEW_SPACE_ACTIONS)
        default:
          return false
      }
    },
    checkCommentPermission(permission: Permission, comment?: Comment, givenSpace?: Space): boolean {
      const user = this.getCurrentUser()
      const space: Space = givenSpace || useSpaceStore().currentSpace!
      if (!user) {
        return false
      }

      const canCreateComments = this.checkSpacePermission(Permission.CREATE_COMMENT, space!!)

      switch (permission) {
        case Permission.CREATE_COMMENT:
          return canCreateComments
        case Permission.UPDATE_COMMENT:
          return canCreateComments && comment?.createdBy === user.id
        case Permission.DELETE_COMMENT:
          return canCreateComments && (comment?.createdBy === user.id || !this.isNotInternalOrPreviewMode())
        case Permission.RESOLVE_COMMENT:
          return canCreateComments && !this.isNotInternalOrPreviewMode()
        default:
          return false
      }
    },
    /**
     * A space page is locked once any signature on that page has been completed. There is no persisted
     * `locked` flag — `signedAt` is the flag, and Unlock (clear-signatures) nulls it for that page only.
     * Signatures on a sibling page never lock this one.
     *
     * Every signature element on the page counts, not just the first: nothing stops a page holding
     * more than one, and inspecting only `find()`'s result left a page whose *second* signature was
     * signed showing SignatureBanner's signed state while staying fully editable.
     */
    isSpacePageLocked(pageId: string | undefined | null, allElements?: Element[]): boolean {
      if (!pageId) {
        return false
      }
      const ids = allElements ? lockedSpacePageIdsFrom(allElements) : this.lockedSpacePageIds
      return ids.has(pageId)
    },
    /**
     * `permission` on a block, for use in computeds and templates. A space-page block is answered from a per-page
     * cache that a computed tracks as one dependency, so blocks never subscribe to the element list or the space
     * list (see util/isolated-page-permissions). Other canvases (tasks, task templates) are small and use
     * `checkPermissionForElement`.
     */
    checkElementPermission(element: Element, permission: Permission): boolean {
      if (element.rootEntityType === RootEntityType.SPACE_PAGE_TEMPLATE && element.rootEntityId) {
        // A template's answer reads the template list and the team role only. Passing the element list would
        // subscribe every block to all of them, which is what froze a 250-block template on open and on close.
        return isolatedPagePermission(`template:${element.rootEntityId}:${permission}`, () =>
          this.checkPermissionForElement(element, [], permission)
        )
      }
      if (element.rootEntityType !== RootEntityType.SPACE_PAGE) {
        return this.checkPermissionForElement(element, useElementStore().elements, permission)
      }
      const pageId = element.rootEntityId
      if (!pageId) {
        return false
      }
      return isolatedPagePermission(`${pageId}:${permission}`, () => this.checkSpacePagePermission(pageId, permission))
    },
    /** MANAGE_ELEMENTS on a space-page block; see `checkElementPermission`. */
    canManageSpacePageElement(element: Element): boolean {
      return this.checkElementPermission(element, Permission.MANAGE_ELEMENTS)
    },
    /** `permission` on the content of a space page: false while a signature locks the page. */
    checkSpacePagePermission(pageId: string, permission: Permission): boolean {
      if (this.isSpacePageLocked(pageId)) {
        return false
      }
      const spaceStore = useSpaceStore()
      const page = spaceStore.spacePageByPageId(pageId)
      const space = spaceStore.spaces.find(s => s.id === page?.spaceId)
      return this.checkSpacePermission(permission, space)
    },
    /**
     * Variable schema is space-scoped, so it freezes once anything in the document is signed
     * (mirrors BE SpaceSignatureLockService.isSpaceSigned). Sibling pages stay content-editable
     * via isSpacePageLocked; this flag only gates create/modify/delete/assignment of variables.
     */
    isSpaceSigned(allElements: Element[]): boolean {
      return allElements.some(e => e.type === 'signature' && e.signatureList?.signatures?.some(s => s.signedAt))
    },
    /** Page ids that currently contribute to the space-wide schema freeze. */
    signedPageIds(allElements: Element[]): string[] {
      return [...lockedSpacePageIdsFrom(allElements)]
    },
    // TODO: _isInPreviewMode and elements used to force reactivity refactor later
    checkPermissionForElement(
      element: Element,
      allElements: Element[],
      permission: Permission,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _isInPreviewMode?: boolean
    ): boolean {
      const spaceStore = useSpaceStore()

      switch (element.rootEntityType) {
        case RootEntityType.SPACE_PAGE: {
          if (isStoreElementList(allElements)) {
            return this.checkSpacePagePermission(element.rootEntityId!!, permission)
          }
          if (this.isSpacePageLocked(element.rootEntityId, allElements)) {
            return false
          }
          const page = spaceStore.spacePageByPageId(element.rootEntityId!!)
          const space = spaceStore.spaces.find(s => s.id === page?.spaceId)
          return this.checkSpacePermission(permission, space)
        }
        case RootEntityType.TASK: {
          const task = useTaskStore().tasks.find(t => t.id === element.rootEntityId!!)
          const space = task?.spaceId ? spaceStore.activeSpaces.find((w: Space) => w.id === task?.spaceId) : undefined
          return this.checkTaskPermission(permission, task, space)
        }
        case RootEntityType.SPACE_PAGE_TEMPLATE: {
          const spacePageTemplate = useSpacePageTemplateStore().spacePageTemplatesById(element.rootEntityId!!)
          if (spacePageTemplate?.isGlobal) {
            return this.checkTeamPermission(Permission.MANAGE_GLOBAL_SPACE_PAGE_TEMPLATES)
          } else {
            return this.checkTeamPermission(Permission.MANAGE_SPACE_PAGE_TEMPLATES)
          }
        }
        case RootEntityType.TASK_TEMPLATE: {
          const taskTemplate = useTaskTemplateStore().taskTemplates.find(t => t.id === element.rootEntityId!!)
          const taskListElement = allElements.find(e => e.id === taskTemplate!!.taskListId)
          if (taskListElement) {
            const spacePageTemplate = useSpacePageTemplateStore().spacePageTemplatesById(
              taskListElement!!.rootEntityId!!
            )
            if (spacePageTemplate?.isGlobal) {
              return this.checkTeamPermission(Permission.MANAGE_GLOBAL_SPACE_PAGE_TEMPLATES)
            } else {
              return this.checkTeamPermission(Permission.MANAGE_TASK_TEMPLATES)
            }
          } else {
            return false
          }
        }
        default:
          return false
      }
    },
    checkResourcePermission(permission: Permission, element: Element, resource?: Resource): boolean {
      switch (permission) {
        case Permission.MANAGE_ELEMENTS:
          return this.checkElementPermission(element, Permission.MANAGE_ELEMENTS)
        case Permission.ADD_CLIENT_RESOURCE:
          return this.checkElementPermission(element, Permission.ADD_CLIENT_RESOURCE)
        case Permission.DELETE_CLIENT_RESOURCE:
          return (
            resource?.createdBy === useUserStore().currentUser!.id ||
            this.checkElementPermission(element, Permission.MANAGE_ELEMENTS)
          )
        default:
          return false
      }
    },
    canChangeRole(user: User): boolean {
      if (!this.checkTeamPermission(Permission.UPDATE_TEAM_SETTINGS)) return false

      const isLastActiveAdmin =
        user.role === TeamRole.ADMIN &&
        !this.getUsers().find(u => u.role === TeamRole.ADMIN && u.state === UserState.ACTIVE && u.id !== user.id)

      return !isLastActiveAdmin
    },
    canRemoveUser(user: User): boolean {
      if (!this.checkTeamPermission(Permission.UPDATE_TEAM_SETTINGS)) return false

      return user.id !== this.getCurrentUser()!.id || user.state === UserState.PENDING_INVITATION
    }
  }
})

export type PermissionStore = ReturnType<typeof usePermissionStore>
